import React from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';

// redux
import { CourseAliases } from 'redux/schemas/models/course';
import { AppDispatch } from 'redux/store';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { openConfirmationDialog, closeConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getBulkConvertibleLecturePages } from 'redux/actions/lecture-pages';
import { BulkConvertibleLecturePagesResult, BulkConvertLecturePagesScopeType } from 'redux/schemas/models/lecture-page';

// components
import { BulkAddToCollectionSourceType } from 'courses/components/course-lesson-to-collection/bulk-add-to-collection-modal';

export enum BulkConvertEligibilityScenario {
  ELIGIBLE = 'ELIGIBLE',
  DEPENDENT_ACTIVITIES = 'DEPENDENT_ACTIVITIES',
  BOTH_DEPENDENT_AND_UNSUPPORTED = 'BOTH_DEPENDENT_AND_UNSUPPORTED',
  ALL_UNSUPPORTED = 'ALL_UNSUPPORTED',
  SOME_UNSUPPORTED = 'SOME_UNSUPPORTED',
}

type FlatCourseAliases = CourseAliases & {
  lecturesAlias?: string;
  LecturesAlias?: string;
};

type CheckBulkConvertibleLecturePagesEligibilityParams = {
  dispatch: AppDispatch;
  aliases: FlatCourseAliases;
  courseId: number;
  sourceType: BulkAddToCollectionSourceType;
  sourceId: number;
  onProceed: (convertibleLecturePagesCount: number) => void;
};

export const getScopeType = (
  sourceType: BulkAddToCollectionSourceType,
): BulkConvertLecturePagesScopeType | null => {
  switch (sourceType) {
    case BulkAddToCollectionSourceType.COURSE:
      return 'course';
    case BulkAddToCollectionSourceType.SECTION:
      return 'lecture_section';
    case BulkAddToCollectionSourceType.SUBSECTION:
      return 'lecture_subsection';
    default:
      return null;
  }
};

const overlayStyles = css`
  max-width: 455px;
`;

export const checkBulkConvertibleLecturePagesEligibility = ({
  dispatch,
  aliases,
  courseId,
  sourceType,
  sourceId,
  onProceed,
}: CheckBulkConvertibleLecturePagesEligibilityParams) => {
  const scopeType = getScopeType(sourceType);
  if (!scopeType) return;

  const getLessonNames = (pages: [number, string][]) => pages.map(([, name]) => name);

  const deriveScenario = (result): BulkConvertEligibilityScenario => {
    const {
      convertibleLecturePagesCount = 0,
      nonConvertibleLecturePages = [],
      associatedLecturePages = [],
    } = result;

    const hasUnsupported = nonConvertibleLecturePages.length > 0;
    const hasDependent = associatedLecturePages.length > 0;

    if (convertibleLecturePagesCount === 0) return BulkConvertEligibilityScenario.ALL_UNSUPPORTED;
    if (hasDependent && hasUnsupported) return BulkConvertEligibilityScenario.BOTH_DEPENDENT_AND_UNSUPPORTED;
    if (hasDependent) return BulkConvertEligibilityScenario.DEPENDENT_ACTIVITIES;
    if (hasUnsupported) return BulkConvertEligibilityScenario.SOME_UNSUPPORTED;
    return BulkConvertEligibilityScenario.ELIGIBLE;
  };

  /* ---------- BODY Text ---------- */

  const bodyTextForDependentActivities = (lessonsWithDependentActivities: string[]) => (
    <div className='d-flex flex-column text-left text-large-body font-weight-normal'>
      <div>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.LESSONS_WITH_DEPENDENT_ACTIVITIES_DESCRIPTION(aliases)}</div>
      <ul>
        {lessonsWithDependentActivities.map((lessonName) => (
          <li key={lessonName}>{lessonName}</li>
        ))}
      </ul>

    </div>
  );

  const bodyTextForLessonsWithBothUnSupportedAndDependentActivities = (lessonsWithDependentActivities: string[], lessonsWithUnSupportedActivities: string[]) => (
    <div className='d-flex flex-column text-left text-large-body font-weight-normal'>
      <div>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.LESSONS_WITH_DEPENDENT_ACTIVITIES_DESCRIPTION(aliases)}</div>
      <ul>
        {lessonsWithDependentActivities.map((lessonName) => (
          <li key={lessonName}>{lessonName}</li>
        ))}
      </ul>
      <div>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.LESSONS_WITH_UNSUPPORTED_COMPONENTS_DESCRIPTION(aliases)}</div>
      <ul>
        {lessonsWithUnSupportedActivities.map((lessonName) => (
          <li key={lessonName}>{lessonName}</li>
        ))}
      </ul>
      <div className='mt-6'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.ARE_YOU_SURE()}</div>
    </div>
  );

  const bodyTextForSomeLessonsCannotBeAdded = (lessonsWithUnSupportedActivities: string[], unsupportedLessonsCount: number) => (
    <div className='d-flex flex-column text-left text-large-body font-weight-normal'>
      <div>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.SOME_LESSONS_CANNOT_BE_ADDED_DESCRIPTION({
        lessonsCount: unsupportedLessonsCount,
        lecturesAlias: aliases.lecturesAlias,
      })}
      </div>
      <ul>
        {lessonsWithUnSupportedActivities.map((lessonName) => (
          <li key={lessonName}>{lessonName}</li>
        ))}
      </ul>
      <div className='mt-6'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.ARE_YOU_SURE()}</div>
    </div>
  );

  /* ---------- SCENARIO HANDLER ---------- */

  const handleScenario = (type: BulkConvertEligibilityScenario, dependentLessons: string[], unsupportedLessons: string[], unsupportedLessonsCount: number, convertibleLecturePagesCount: number) => {
    switch (type) {
      case BulkConvertEligibilityScenario.ELIGIBLE:
        onProceed(convertibleLecturePagesCount);
        break;
      case BulkConvertEligibilityScenario.DEPENDENT_ACTIVITIES:
        dispatch(
          openConfirmationDialog({
            icon: 'info',
            title: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.IMPORTANT_NOTE_TITLE(aliases),
            bodyText: bodyTextForDependentActivities(dependentLessons),
            confirmText: t.TIMELINE.ADD_TO_COLLECTION_WARNING_MODAL.GOT_IT(),
            confirmButtonVariant: 'primary',
            noCancelButton: true,
            onConfirm: () => onProceed(convertibleLecturePagesCount),
          }),
        );
        break;
      case BulkConvertEligibilityScenario.BOTH_DEPENDENT_AND_UNSUPPORTED:
        dispatch(
          openConfirmationDialog({
            icon: 'info',
            title: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.IMPORTANT_NOTE_TITLE(aliases),
            bodyText: bodyTextForLessonsWithBothUnSupportedAndDependentActivities(dependentLessons, unsupportedLessons),
            confirmText: t.FORM.YES_SURE(),
            confirmButtonVariant: 'primary',
            cancelText: t.FORM.CANCEL(),
            onConfirm: () => onProceed(convertibleLecturePagesCount),
          }),
        );
        break;
      case BulkConvertEligibilityScenario.ALL_UNSUPPORTED:
        dispatch(
          openConfirmationDialog({
            icon: 'ban',
            title: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.ALL_LESSONS_CANNOT_BE_ADDED_TITLE(aliases),
            bodyText: (
              <div css={overlayStyles} className='all-lessons-cannot-be-added-body-text text-large-body'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.ALL_LESSONS_CANNOT_BE_ADDED_DESCRIPTION(aliases)}</div>
            ),
            confirmText: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.BACK_TO_COURSE(aliases),
            confirmButtonVariant: 'primary',
            noCancelButton: true,
            onConfirm: () => {
              dispatch(closeConfirmationDialog());
            },
          }),
        );
        break;
      case BulkConvertEligibilityScenario.SOME_UNSUPPORTED:
        dispatch(
          openConfirmationDialog({
            icon: 'info',
            title: t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.WARNINGS.SOME_LESSONS_CANNOT_BE_ADDED_TITLE(aliases),
            bodyText: bodyTextForSomeLessonsCannotBeAdded(unsupportedLessons, unsupportedLessonsCount),
            confirmText: t.FORM.YES_SURE(),
            confirmButtonVariant: 'primary',
            cancelText: t.FORM.CANCEL(),
            onConfirm: () => onProceed(convertibleLecturePagesCount),
          }),
        );
        break;
      default:
        break;
    }
  };

  /* ---------- API CALL ---------- */

  dispatch(getBulkConvertibleLecturePages({
    courseId,
    scopeType,
    scopeId: sourceId,
  }))
    .unwrap()
    .then((result: BulkConvertibleLecturePagesResult) => {
      const { convertibleLecturePagesCount, associatedLecturePages } = result;
      const totalConvertibleLecturePagesCount = convertibleLecturePagesCount + associatedLecturePages.length;
      const dependentLessons = getLessonNames(result.associatedLecturePages);
      const unsupportedLessons = getLessonNames(result.nonConvertibleLecturePages);
      const unsupportedLessonsCount = result.nonConvertibleLecturePages.length;

      const scenario = deriveScenario(result);

      handleScenario(
        scenario,
        dependentLessons,
        unsupportedLessons,
        unsupportedLessonsCount,
        totalConvertibleLecturePagesCount,
      );
    })
    .catch(() => {
      dispatch(addAlertMessage({
        header: t.FORM.ERROR(),
        type: AlertMessageType.ERROR,
      }));
    });
};

